/**
 * Centralized Mock Data for AcuSightPro Demo
 * All VA-specific sample data for the demo application
 */

// ==================== CONTRACT ANALYZER ====================
export const sampleContractAnalysis = {
  contractName: 'VA T4NG - EHR Integration Services',
  vendor: 'Cerner Federal Health LLC',
  contractNumber: 'VA118-16-D-1234',
  effectiveDate: '2023-01-15',
  expirationDate: '2028-01-14',
  totalValue: '$12,500,000',
  riskScore: 72,
  overallStatus: 'Attention Needed',
  keyDates: [
    { date: '2025-01-14', event: 'Option Year 2 Renewal Deadline', daysAway: 15, priority: 'high' },
    { date: '2025-02-28', event: 'VA Directive 6500 Recertification', daysAway: 60, priority: 'medium' },
    { date: '2025-03-15', event: 'HIPAA Compliance Audit', daysAway: 75, priority: 'medium' },
    { date: '2025-06-30', event: 'Quarterly CPARS Review', daysAway: 182, priority: 'low' },
  ],
  risks: [
    { id: 1, category: 'Compliance', severity: 'high', title: 'VA Directive 6500 Gap Identified', description: 'Continuous monitoring requirements under VA Handbook 6500.3 not fully addressed in current security plan. ATO renewal at risk.', recommendation: 'Schedule immediate review with VA NSOC and update System Security Plan (SSP) within 30 days.' },
    { id: 2, category: 'Interoperability', severity: 'high', title: 'FHIR R4 Implementation Delay', description: 'HL7 FHIR R4 APIs for DoD/VA health data exchange behind schedule. Affects Joint HIE milestones.', recommendation: 'Escalate to PMO and request updated implementation timeline with weekly progress reports.' },
    { id: 3, category: 'Financial', severity: 'medium', title: 'SDVOSB Subcontracting Goal at Risk', description: 'Current SDVOSB subcontracting at 8%, below 15% contract requirement.', recommendation: 'Identify additional SDVOSB partners and submit revised subcontracting plan to CO.' },
    { id: 4, category: 'Performance', severity: 'medium', title: 'System Availability Below SLA', description: 'Month-over-month availability at 99.2%, below 99.9% SLA threshold. Three P1 incidents in last quarter.', recommendation: 'Request root cause analysis and corrective action plan. Consider performance improvement plan.' },
    { id: 5, category: 'Legal', severity: 'low', title: 'VAAR Clause Update Required', description: 'References VAAR 852.211-70 (2020 version), superseded by 2024 update.', recommendation: 'Include updated VAAR clause in next contract modification.' },
  ],
  clauses: [
    { id: 1, section: '2.1', title: 'Scope of Work', summary: 'EHR integration services for 18 VISN regions including Cerner Millennium implementation, legacy VistA data migration, and Community Care Network connectivity.', status: 'standard' },
    { id: 2, section: '3.1', title: 'Period of Performance', summary: 'Base year plus 4 option years. Current status: Option Year 1. Full deployment target: 170 VA Medical Centers.', status: 'standard' },
    { id: 3, section: '4.1', title: 'Pricing Structure', summary: 'FFP for implementation milestones, T&M for surge support and enhancements. CLIN 0001-0015 defined.', status: 'attention' },
    { id: 4, section: '5.1', title: 'Security Requirements', summary: 'VA Directive 6500 compliance mandatory. FedRAMP High required. HIPAA/HITECH compliance. Continuous ATO with VA NSOC integration.', status: 'warning' },
    { id: 5, section: '5.2', title: 'Privacy Requirements', summary: 'Privacy Act, 38 U.S.C. 5701, 38 U.S.C. 7332 (patient records confidentiality). Annual privacy training for all contractor personnel.', status: 'standard' },
    { id: 6, section: '6.1', title: 'Deliverables', summary: 'Weekly status reports, monthly executive briefings, quarterly CPARS input, Go-Live readiness assessments per facility.', status: 'standard' },
    { id: 7, section: '7.1', title: 'Service Level Agreements', summary: 'System Availability: 99.9%, P1 Response: 15 minutes, P1 Resolution: 4 hours, Scheduled Downtime: <4 hours/month.', status: 'attention' },
    { id: 8, section: '8.1', title: 'SDVOSB/VOSB Requirements', summary: 'SDVOSB subcontracting goal: 15%, VOSB goal: 5%. Quarterly reporting to VA OSDBU required.', status: 'warning' },
  ],
  recommendations: [
    'Schedule VA Directive 6500 compliance review with ISSO within 2 weeks',
    'Escalate FHIR R4 implementation delays to VA OIT leadership',
    'Submit revised SDVOSB subcontracting plan to increase from 8% to 15%',
    'Request vendor corrective action plan for system availability issues',
    'Initiate Option Year 2 renewal planning with updated SOW requirements',
    'Coordinate with VA OSDBU on small business goal remediation'
  ]
};

export const sampleContracts = ['VA T4NG EHR Services', 'VA Community Care Network', 'VBA Claims Processing BPA'];

// ==================== TEAM WORKSPACE ====================
export const workspacesData = [
  {
    id: 1,
    name: 'VISN 8 EHR Modernization',
    type: 'acquisition',
    status: 'active',
    description: 'Electronic Health Record system modernization for Veterans Integrated Service Network 8',
    members: 12,
    documents: 34,
    lastActivity: '2 hours ago',
    deadline: '2025-03-15',
    progress: 68,
    lead: 'Sarah Johnson',
    tags: ['T4NG', 'Healthcare IT', 'High Priority']
  },
  {
    id: 2,
    name: 'VA Telehealth Expansion',
    type: 'planning',
    status: 'active',
    description: 'Expanding telehealth capabilities across all VA Medical Centers',
    members: 8,
    documents: 18,
    lastActivity: '5 hours ago',
    deadline: '2025-06-30',
    progress: 35,
    lead: 'Michael Chen',
    tags: ['Telehealth', 'Modernization']
  },
  {
    id: 3,
    name: 'VBA Claims Automation',
    type: 'evaluation',
    status: 'active',
    description: 'AI-powered claims processing automation for Veterans Benefits Administration',
    members: 15,
    documents: 56,
    lastActivity: '1 day ago',
    deadline: '2025-02-28',
    progress: 82,
    lead: 'Emily Davis',
    tags: ['AI/ML', 'Claims', 'VBA']
  },
  {
    id: 4,
    name: 'VA Cybersecurity Enhancement',
    type: 'acquisition',
    status: 'active',
    description: 'Enterprise cybersecurity improvements aligned with VA Directive 6500',
    members: 10,
    documents: 28,
    lastActivity: '3 days ago',
    deadline: '2025-04-30',
    progress: 45,
    lead: 'Robert Brown',
    tags: ['Security', 'VA 6500', 'FedRAMP']
  },
  {
    id: 5,
    name: 'Community Care Network',
    type: 'planning',
    status: 'active',
    description: 'Third-party community care provider network expansion',
    members: 6,
    documents: 12,
    lastActivity: '1 week ago',
    deadline: '2025-09-30',
    progress: 15,
    lead: 'Jennifer Martinez',
    tags: ['Community Care', 'Network']
  },
  {
    id: 6,
    name: 'Legacy VistA Migration',
    type: 'evaluation',
    status: 'completed',
    description: 'Migration from legacy VistA systems to modern EHR platform',
    members: 20,
    documents: 89,
    lastActivity: '2 weeks ago',
    deadline: '2024-12-31',
    progress: 100,
    lead: 'David Wilson',
    tags: ['VistA', 'Migration', 'Complete']
  }
];

export const teamMembersData = [
  { id: 1, name: 'Sarah Johnson', role: 'Program Manager', avatar: 'SJ', status: 'online' },
  { id: 2, name: 'Michael Chen', role: 'Contracting Officer', avatar: 'MC', status: 'online' },
  { id: 3, name: 'Emily Davis', role: 'Technical Lead', avatar: 'ED', status: 'away' },
  { id: 4, name: 'Robert Brown', role: 'Security Specialist', avatar: 'RB', status: 'offline' },
  { id: 5, name: 'Jennifer Martinez', role: 'Business Analyst', avatar: 'JM', status: 'online' },
  { id: 6, name: 'David Wilson', role: 'Legal Counsel', avatar: 'DW', status: 'offline' },
  { id: 7, name: 'Amanda Taylor', role: 'Finance Specialist', avatar: 'AT', status: 'online' },
  { id: 8, name: 'James Anderson', role: 'Quality Assurance', avatar: 'JA', status: 'away' }
];

export const workspaceRoles = [
  { id: 'viewer', name: 'Viewer', description: 'Can view documents and comments', color: 'gray' },
  { id: 'contributor', name: 'Contributor', description: 'Can edit documents and add comments', color: 'blue' },
  { id: 'reviewer', name: 'Reviewer', description: 'Can approve documents and manage reviews', color: 'green' },
  { id: 'co', name: 'Contracting Officer', description: 'Full authority on procurement actions', color: 'purple' },
  { id: 'pm', name: 'Program Manager', description: 'Oversees program execution and milestones', color: 'indigo' },
  { id: 'legal', name: 'Legal Counsel', description: 'Reviews legal compliance and terms', color: 'amber' },
  { id: 'finance', name: 'Finance Specialist', description: 'Manages budget and financial reviews', color: 'emerald' },
  { id: 'security', name: 'Security Officer', description: 'Reviews security requirements and compliance', color: 'red' },
  { id: 'admin', name: 'Workspace Admin', description: 'Full access including member management', color: 'rose' }
];

// ==================== DOCUMENT LIBRARY ====================
export const documentsData = [
  { id: 1, name: 'VA EHR RFP Template', type: 'template', category: 'RFP', size: '2.4 MB', modified: '2024-01-10', author: 'Sarah Johnson', status: 'approved', tags: ['EHR', 'T4NG', 'Healthcare'] },
  { id: 2, name: 'SDVOSB Compliance Checklist', type: 'checklist', category: 'Compliance', size: '156 KB', modified: '2024-01-08', author: 'Emily Davis', status: 'draft', tags: ['SDVOSB', 'Compliance'] },
  { id: 3, name: 'VA Directive 6500 Requirements', type: 'policy', category: 'Security', size: '1.8 MB', modified: '2024-01-05', author: 'Robert Brown', status: 'approved', tags: ['Security', 'VA 6500', 'ATO'] },
  { id: 4, name: 'T4NG Task Order Guide', type: 'guide', category: 'Contract', size: '890 KB', modified: '2024-01-03', author: 'Michael Chen', status: 'approved', tags: ['T4NG', 'Task Order'] },
  { id: 5, name: 'HIPAA Compliance Template', type: 'template', category: 'Compliance', size: '1.2 MB', modified: '2023-12-28', author: 'Jennifer Martinez', status: 'approved', tags: ['HIPAA', 'Healthcare', 'Privacy'] },
  { id: 6, name: 'Source Selection Plan Template', type: 'template', category: 'Evaluation', size: '567 KB', modified: '2023-12-20', author: 'David Wilson', status: 'approved', tags: ['Evaluation', 'Source Selection'] },
  { id: 7, name: 'VA Market Research Report', type: 'report', category: 'Market Research', size: '3.1 MB', modified: '2023-12-15', author: 'Amanda Taylor', status: 'in_review', tags: ['Market Research', 'VA'] },
  { id: 8, name: 'IGCE Template - IT Services', type: 'template', category: 'Cost', size: '234 KB', modified: '2023-12-10', author: 'James Anderson', status: 'approved', tags: ['IGCE', 'Cost Estimate'] },
  { id: 9, name: 'VISN 8 EHR RFI Draft', type: 'document', category: 'RFI', size: '1.5 MB', modified: '2024-01-12', author: 'Sarah Johnson', status: 'draft', tags: ['RFI', 'VISN 8', 'EHR'] },
  { id: 10, name: 'Telehealth SOW Template', type: 'template', category: 'SOW', size: '678 KB', modified: '2024-01-09', author: 'Michael Chen', status: 'approved', tags: ['Telehealth', 'SOW'] },
  { id: 11, name: 'VBA Claims AI Requirements', type: 'document', category: 'Requirements', size: '945 KB', modified: '2024-01-11', author: 'Emily Davis', status: 'in_review', tags: ['AI/ML', 'VBA', 'Claims'] },
  { id: 12, name: 'FedRAMP Authorization Guide', type: 'guide', category: 'Security', size: '2.1 MB', modified: '2023-12-22', author: 'Robert Brown', status: 'approved', tags: ['FedRAMP', 'Cloud', 'Security'] }
];

export const documentCategories = [
  { name: 'All Documents', count: 12, icon: 'folder' },
  { name: 'Templates', count: 5, icon: 'template' },
  { name: 'Compliance', count: 2, icon: 'shield' },
  { name: 'Security', count: 2, icon: 'lock' },
  { name: 'Contract', count: 1, icon: 'document' },
  { name: 'Evaluation', count: 1, icon: 'clipboard' },
  { name: 'Cost', count: 1, icon: 'dollar' }
];

// ==================== SDVOSB TRACKER ====================
export const sdvosbData = {
  currentFY: 'FY2025',
  goals: {
    sdvosb: { target: 15, current: 12.3, trend: 'up' },
    vosb: { target: 5, current: 4.8, trend: 'up' },
    smallBusiness: { target: 23, current: 28.5, trend: 'up' },
    wosb: { target: 5, current: 6.2, trend: 'stable' },
    hubzone: { target: 3, current: 2.1, trend: 'down' },
    '8a': { target: 5, current: 5.5, trend: 'up' }
  },
  totalObligations: '$278,500,000',
  sdvosbObligations: '$34,255,500',
  quarterlyProgress: [
    { quarter: 'Q1', sdvosb: 10.2, vosb: 4.1, target: 15 },
    { quarter: 'Q2', sdvosb: 11.5, vosb: 4.5, target: 15 },
    { quarter: 'Q3', sdvosb: 12.3, vosb: 4.8, target: 15 },
    { quarter: 'Q4', sdvosb: null, vosb: null, target: 15 }
  ]
};

export const sdvosbContractsData = [
  { id: 1, contractor: 'Veteran IT Solutions LLC', contractNumber: 'VA-SDVOSB-2024-001', value: '$4,500,000', type: 'SDVOSB', status: 'active', awardDate: '2024-02-15', endDate: '2027-02-14', description: 'IT Support Services for VISN 8' },
  { id: 2, contractor: 'Heroes Healthcare Tech', contractNumber: 'VA-SDVOSB-2024-002', value: '$2,800,000', type: 'SDVOSB', status: 'active', awardDate: '2024-03-01', endDate: '2026-02-28', description: 'Telehealth Platform Development' },
  { id: 3, contractor: 'Patriot Cyber Services', contractNumber: 'VA-VOSB-2024-003', value: '$1,200,000', type: 'VOSB', status: 'active', awardDate: '2024-01-10', endDate: '2025-01-09', description: 'Cybersecurity Assessment Services' },
  { id: 4, contractor: 'Freedom Data Systems', contractNumber: 'VA-SDVOSB-2023-045', value: '$3,200,000', type: 'SDVOSB', status: 'active', awardDate: '2023-09-15', endDate: '2026-09-14', description: 'Data Analytics Platform' },
  { id: 5, contractor: 'Valor Medical Supplies', contractNumber: 'VA-SDVOSB-2024-004', value: '$890,000', type: 'SDVOSB', status: 'pending', awardDate: '2024-04-01', endDate: '2025-03-31', description: 'Medical Equipment Maintenance' },
  { id: 6, contractor: 'Service First Consulting', contractNumber: 'VA-VOSB-2024-005', value: '$1,500,000', type: 'VOSB', status: 'active', awardDate: '2024-02-20', endDate: '2026-02-19', description: 'Program Management Support' }
];

export const certificationData = [
  { id: 1, contractor: 'Veteran IT Solutions LLC', certType: 'SDVOSB', verificationNumber: 'V-2024-00123', status: 'verified', expirationDate: '2025-06-30', lastVerified: '2024-06-30' },
  { id: 2, contractor: 'Heroes Healthcare Tech', certType: 'SDVOSB', verificationNumber: 'V-2024-00456', status: 'verified', expirationDate: '2025-08-15', lastVerified: '2024-08-15' },
  { id: 3, contractor: 'Patriot Cyber Services', certType: 'VOSB', verificationNumber: 'V-2024-00789', status: 'verified', expirationDate: '2025-03-01', lastVerified: '2024-03-01' },
  { id: 4, contractor: 'Freedom Data Systems', certType: 'SDVOSB', verificationNumber: 'V-2023-01234', status: 'expiring', expirationDate: '2025-01-31', lastVerified: '2024-01-31' },
  { id: 5, contractor: 'Valor Medical Supplies', certType: 'SDVOSB', verificationNumber: 'V-2024-00567', status: 'pending', expirationDate: '2025-09-30', lastVerified: '2024-09-30' },
  { id: 6, contractor: 'Service First Consulting', certType: 'VOSB', verificationNumber: 'V-2024-00890', status: 'verified', expirationDate: '2025-07-15', lastVerified: '2024-07-15' }
];

// ==================== CALENDAR & DEADLINES ====================
export const deadlinesData = [
  {
    id: 1,
    title: 'T4NG Task Order - VISN 8 EHR',
    type: 'submission',
    date: '2026-04-20',
    time: '5:00 PM EST',
    project: 'VISN 8 EHR Modernization',
    priority: 'high',
    status: 'upcoming',
    description: 'Final proposal submission for EHR integration services',
    assignee: 'Sarah Johnson'
  },
  {
    id: 2,
    title: 'VA Directive 6500 ATO Renewal',
    type: 'compliance',
    date: '2026-04-25',
    time: '11:59 PM EST',
    project: 'VA Cybersecurity & ATO',
    priority: 'high',
    status: 'upcoming',
    description: 'Authority to Operate renewal documentation due',
    assignee: 'Michael Chen'
  },
  {
    id: 3,
    title: 'SDVOSB Subcontracting Report',
    type: 'report',
    date: '2026-05-01',
    time: '5:00 PM EST',
    project: 'All Projects',
    priority: 'medium',
    status: 'upcoming',
    description: 'Quarterly SDVOSB subcontracting compliance report to VA OSDBU',
    assignee: 'Emily Davis'
  },
  {
    id: 4,
    title: 'Telehealth RFI Response Due',
    type: 'submission',
    date: '2026-05-06',
    time: '2:00 PM EST',
    project: 'VHA Telehealth Expansion',
    priority: 'high',
    status: 'upcoming',
    description: 'RFI response for telehealth platform capabilities',
    assignee: 'Robert Brown'
  },
  {
    id: 5,
    title: 'CPARS Input - Claims Automation',
    type: 'review',
    date: '2026-05-11',
    time: '5:00 PM EST',
    project: 'VBA Claims Automation',
    priority: 'medium',
    status: 'upcoming',
    description: 'Contractor Performance Assessment input due',
    assignee: 'James Wilson'
  },
  {
    id: 6,
    title: 'HIPAA Compliance Audit',
    type: 'compliance',
    date: '2026-05-16',
    time: '9:00 AM EST',
    project: 'VISN 8 EHR Modernization',
    priority: 'high',
    status: 'upcoming',
    description: 'Annual HIPAA compliance audit for health IT systems',
    assignee: 'Karen White'
  },
  {
    id: 7,
    title: 'Option Year Exercise Decision',
    type: 'decision',
    date: '2026-05-21',
    time: '5:00 PM EST',
    project: 'IT Support Services',
    priority: 'high',
    status: 'upcoming',
    description: 'Decision required on exercising Option Year 2',
    assignee: 'Sarah Johnson'
  },
  {
    id: 8,
    title: 'Security Assessment Complete',
    type: 'milestone',
    date: '2026-05-29',
    time: 'EOD',
    project: 'VA Cybersecurity & ATO',
    priority: 'medium',
    status: 'upcoming',
    description: 'Third-party security assessment completion deadline',
    assignee: 'Michael Chen'
  },
  {
    id: 9,
    title: 'FY26 Q2 Small Business Report',
    type: 'report',
    date: '2026-06-13',
    time: '5:00 PM EST',
    project: 'All Projects',
    priority: 'medium',
    status: 'upcoming',
    description: 'Quarterly small business goal achievement report',
    assignee: 'Emily Davis'
  },
  {
    id: 10,
    title: 'Contract Closeout - Legacy System',
    type: 'milestone',
    date: '2026-06-29',
    time: 'EOD',
    project: 'Legacy System Migration',
    priority: 'low',
    status: 'upcoming',
    description: 'Final closeout activities for legacy system contract',
    assignee: 'Thomas Garcia'
  }
  ,
  {
    id: 101,
    title: 'VAMC Denver Housekeeping - Option Year 1 Decision',
    type: 'decision',
    date: '2026-07-01',
    time: '5:00 PM EST',
    project: 'VAMC Denver Housekeeping (SDVOSB)',
    priority: 'high',
    status: 'upcoming',
    description: 'Decision required on exercise of Option Year 1. Vendor CPARS 4.6 Exceptional. Recommend exercise.',
    assignee: 'Sarah Miller'
  },
  {
    id: 102,
    title: 'VA Pittsburgh Pharmacy - Option Year 3 Decision',
    type: 'decision',
    date: '2026-06-15',
    time: '12:00 PM EST',
    project: 'VA Pittsburgh Pharmacy Software',
    priority: 'medium',
    status: 'upcoming',
    description: 'Year 2-to-3 option decision. CPARS 4.0 Very Good. MedTrack Government Solutions.',
    assignee: 'Sarah Miller'
  },
  {
    id: 103,
    title: 'VAMC Tucson Cybersecurity - Closeout',
    type: 'milestone',
    date: '2026-06-08',
    time: '3:00 PM EST',
    project: 'VAMC Tucson Cybersecurity Risk Assessment',
    priority: 'medium',
    status: 'upcoming',
    description: 'Contract closing. 8(a) sole source. Eligible for recompete or follow-on.',
    assignee: 'James Chen'
  },
  {
    id: 104,
    title: 'VA OIT Help Desk Recompete - Industry Day',
    type: 'meeting',
    date: '2026-06-04',
    time: '10:00 AM EST',
    project: 'VA OIT Help Desk Support',
    priority: 'high',
    status: 'upcoming',
    description: 'Recompete industry day. Continental IT Solutions current incumbent. CPARS trending down.',
    assignee: 'Patricia Wong'
  },
  {
    id: 105,
    title: 'VHA Specialty Care Telehealth - Q2 Review',
    type: 'review',
    date: '2026-05-28',
    time: '2:00 PM EST',
    project: 'VHA Specialty Care Telehealth Platform (VISN 19)',
    priority: 'medium',
    status: 'upcoming',
    description: 'Quarterly performance review with TelaHealth Veterans Solutions LLC.',
    assignee: 'Sarah Miller'
  },
  {
    id: 106,
    title: 'VAMC Denver Patient Transport - Final Option Decision',
    type: 'decision',
    date: '2026-06-30',
    time: '5:00 PM EST',
    project: 'VAMC Denver Patient Transport Vehicle Lease',
    priority: 'high',
    status: 'upcoming',
    description: 'Final option year decision. Mountain Federal Fleet Services. CPARS 3.8 Satisfactory. Recommend non-renew and recompete.',
    assignee: 'Sarah Miller'
  }
];

export const deadlineTypeColors = {
  submission: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  compliance: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  report: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  review: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  decision: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  milestone: { bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500' }
};

// ==================== NOTIFICATIONS ====================
export const sampleNotifications = [
  {
    id: 1,
    type: 'deadline',
    title: 'T4NG Task Order Due Tomorrow',
    message: 'VA VISN 8 EHR Integration - Final proposal submission deadline',
    time: '2 hours ago',
    read: false,
    priority: 'high',
    link: '/submissions'
  },
  {
    id: 2,
    type: 'compliance',
    title: 'VA Directive 6500 Alert',
    message: 'ATO recertification required within 30 days for VISN 8 systems',
    time: '5 hours ago',
    read: false,
    priority: 'medium',
    link: '/settings'
  },
  {
    id: 3,
    type: 'opportunity',
    title: 'New VA Opportunity Match',
    message: 'VHA Telehealth Expansion - 95% match score',
    time: '1 day ago',
    read: false,
    priority: 'medium',
    link: '/portfolio'
  },
  {
    id: 4,
    type: 'submission',
    title: 'Vendor Submission Received',
    message: 'Cerner Federal submitted proposal for Community Care integration',
    time: '1 day ago',
    read: true,
    priority: 'low',
    link: '/submissions'
  },
  {
    id: 5,
    type: 'team',
    title: 'Workspace Invitation',
    message: 'Sarah Johnson invited you to "VISN 8 EHR Modernization"',
    time: '2 days ago',
    read: true,
    priority: 'low',
    link: '/team-workspace'
  },
  {
    id: 6,
    type: 'ai',
    title: 'AI Analysis Complete',
    message: 'Contract risk analysis ready for VA T4NG review',
    time: '3 days ago',
    read: true,
    priority: 'low',
    link: '/contract-analyzer'
  },
  {
    id: 7,
    type: 'meeting',
    title: 'VA EHR Review Meeting',
    message: 'Weekly status review with VA stakeholders - Conference Room A',
    time: 'Today at 2:00 PM',
    read: false,
    priority: 'high',
    link: '/calendar',
    meetingTime: '2:00 PM',
    rsvpStatus: null
  },
  {
    id: 8,
    type: 'meeting',
    title: 'T4NG Proposal Finalization',
    message: 'Final review before submission deadline - Virtual Meeting',
    time: 'Today at 4:30 PM',
    read: false,
    priority: 'high',
    link: '/calendar',
    meetingTime: '4:30 PM',
    rsvpStatus: null
  }
];

// ==================== AI RFI GENERATOR ====================
export const defaultRFIPrompt = `The VA requires a modernized Electronic Health Records (EHR) integration platform to support seamless data exchange between VA Medical Centers and Community Care Network providers.

The solution must integrate with the existing Cerner Millennium system, support HL7 FHIR standards, and provide real-time patient data synchronization across VISN regions.

Must comply with VA Directive 6500, HIPAA, and Section 508 accessibility requirements. Budget range: $2M-$5M. Implementation timeline: FY25 Q2.`;

export const defaultAISuggestions = [
  {
    id: '1',
    title: 'Intelligent Data Analytics Dashboard',
    category: 'Analytics & Insights',
    description: 'AI-powered analytics to automatically identify trends, anomalies, and insights from your data without manual analysis.',
    value: 'Reduces time spent on reporting by 60% and surfaces insights humans might miss.',
    selected: true
  },
  {
    id: '2',
    title: 'Automated Workflow Processing',
    category: 'Automation',
    description: 'Use AI to automate repetitive tasks, route requests intelligently, and reduce manual data entry.',
    value: 'Increases operational efficiency and reduces human error in routine processes.',
    selected: true
  },
  {
    id: '3',
    title: 'Predictive Forecasting & Planning',
    category: 'Predictive Intelligence',
    description: 'Machine learning models to forecast demand, resource needs, and potential issues before they occur.',
    value: 'Enables proactive decision-making and better resource allocation.',
    selected: true
  }
];

export const sampleTemplates = [
  { id: 1, name: 'VA Healthcare IT RFI', description: 'Standard RFI for healthcare technology projects', tags: ['Healthcare', 'IT', 'VA'] },
  { id: 2, name: 'SDVOSB Set-Aside RFP', description: 'RFP template for veteran-owned small business contracts', tags: ['SDVOSB', 'Set-Aside'] },
  { id: 3, name: 'Cybersecurity Services SOW', description: 'Statement of Work for security assessments and compliance', tags: ['Security', 'VA 6500'] }
];

export default {
  sampleContractAnalysis,
  sampleContracts,
  workspacesData,
  teamMembersData,
  workspaceRoles,
  documentsData,
  documentCategories,
  sdvosbData,
  sdvosbContractsData,
  certificationData,
  deadlinesData,
  deadlineTypeColors,
  sampleNotifications,
  defaultRFIPrompt,
  defaultAISuggestions,
  sampleTemplates
};
